export const M5_UNIT_14 = {
  id: "m5-u14-plans-predictions",
  title: "Unit 14: Plans & Predictions",
  topic: "Talking about future plans, predictions, and opinions about entertainment and travel",
  iconType: "crystal-ball",
  grammarImage: "/grammar/grammar-m5-u14.jpg",
  grammarText: "This final unit of Module 5 brings together everything you've learned: going to for future plans (I'm going to visit Paris next year) and predictions (It's going to rain tomorrow), expressing opinions about movies and shows (I think it will be exciting, It looks boring), making invitations with future forms, and discussing travel plans. You'll use time expressions (next week, in two months, by Friday) to specify when things will happen. This unit combines entertainment vocabulary (thriller, comedy, documentary), travel words (flight, hotel, sightseeing), and natural conversation patterns for planning activities with friends. It's the perfect capstone—pulling together grammar, vocabulary, and real-world communication skills into confident, natural English.",

  sets: [
    // ===================================================================
    // SET 1: GOING TO - PLANS (10 questions) - A2
    // ===================================================================
    {
      id: "m5-u14-s1",
      title: "Set 1: Future Plans with Going To",
      questions: [
        {
          type: "match",
          q: "Match future plan structures:",
          pairs: [
            { left: "I'm going to travel", right: "Definite plan" },
            { left: "I want to travel", right: "Desire (not definite)" },
            { left: "I'm traveling", right: "Arranged plan" }
          ],
          a: "completed",
          successtip: "✓ 'Going to' = decided plan (not just a wish!)"
        },
        {
          type: "mc",
          q: "PLANS: 'What are your plans for summer?' 'I ___ visit my grandparents in Spain.'",
          options: ["'m going to", "want", "going"],
          a: "'m going to",
          tip: "'Am/is/are going to' + verb = future plan"
        },
        {
          type: "input",
          q: "HER PLANS: 'She's going to ___ a new language next year.' (learn)",
          a: "learn",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["study"] },
          tip: "After 'going to', use base form of verb"
        },
        {
          type: "order",
          q: "Build future plan:",
          words: ["We're", "going", "to", "buy", "a", "new", "car", "next", "month"],
          a: "We're going to buy a new car next month",
          successtip: "✓ Subject + am/is/are + going to + verb + time"
        },
        {
          type: "mc",
          q: "QUESTION: '___ you going to watch the game tonight?' 'Yes, I am!'",
          options: ["Are", "Do", "Will"],
          a: "Are",
          tip: "Question: Are/Is + subject + going to + verb?",
          successtip: "✓ 'Are you going to...?' (not 'Do you going to...?')"
        },
        {
          type: "input",
          q: "NEGATIVE: 'I'm not going to ___ to the party. I'm too tired.' (go)",
          a: "go",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Negative: am not/isn't/aren't going to + verb"
        },
        {
          type: "match",
          q: "Match questions to answers:",
          pairs: [
            { left: "What are you going to do?", right: "I'm going to study" },
            { left: "When is she going to leave?", right: "She's going to leave tomorrow" },
            { left: "Are they going to come?", right: "Yes, they are" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "THEIR PLANS: 'They ___ get married next summer!'",
          options: ["'re going to", "going to", "will going to"],
          a: "'re going to",
          tip: "They are going to = They're going to"
        },
        {
          type: "input",
          q: "TIME PHRASE: 'He's going to start his new job ___ two weeks.' (in/within)",
          a: "in",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "'In two weeks' = two weeks from now",
          successtip: "✓ 'In + time period' for future: in two days, in three months, in a year"
        },
        {
          type: "order",
          q: "Traduce: ¿Qué vas a hacer este fin de semana?",
          words: ["What", "are", "you", "going", "to", "do", "this", "weekend?"],
          a: "What are you going to do this weekend?",
          successtip: "✓ Perfect! 'Going to' = 'ir a' (futuro planeado)"
        }
      ]
    },

    // ===================================================================
    // SET 2: GOING TO - PREDICTIONS (10 questions) - A2+
    // ===================================================================
    {
      id: "m5-u14-s2",
      title: "Set 2: Making Predictions",
      questions: [
        {
          type: "match",
          q: "Going to has two uses:",
          pairs: [
            { left: "Plans: I'm going to visit Rome", right: "Decided action" },
            { left: "Predictions: It's going to rain", right: "Based on evidence" },
            { left: "Both use same structure!", right: "Context tells you which" }
          ],
          a: "completed",
          successtip: "✓ Same grammar, different meaning! Context is key"
        },
        {
          type: "mc",
          q: "WEATHER: 'Look at those dark clouds! It ___ rain soon.'",
          options: ["'s going to", "rains", "rained"],
          a: "'s going to",
          tip: "Prediction based on evidence (clouds) = going to",
          successtip: "✓ Evidence you can see → 'going to' for prediction!"
        },
        {
          type: "input",
          q: "DANGER: 'Watch out! That box is going to ___!' (fall)",
          a: "fall",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "See it about to happen = prediction with 'going to'"
        },
        {
          type: "order",
          q: "Build prediction:",
          words: ["She", "didn't", "study.", "She's", "going", "to", "fail", "the", "exam."],
          a: "She didn't study. She's going to fail the exam.",
          successtip: "✓ Logical prediction based on fact = going to"
        },
        {
          type: "mc",
          q: "MOVIE PREDICTION: 'This movie looks boring. I don't think I ___ enjoy it.'",
          options: ["'m going to", "enjoy", "enjoyed"],
          a: "'m going to",
          tip: "Based on what you see/know = prediction"
        },
        {
          type: "input",
          q: "SPORTS: 'Our team is playing terribly. We're going to ___ this game.' (lose)",
          a: "lose",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Current evidence → future prediction"
        },
        {
          type: "match",
          q: "Match evidence to prediction:",
          pairs: [
            { left: "He's driving too fast", right: "He's going to have an accident" },
            { left: "She's very talented", right: "She's going to be famous" },
            { left: "They're arguing a lot", right: "They're going to break up" }
          ],
          a: "completed",
          successtip: "✓ Evidence NOW → prediction FUTURE with 'going to'"
        },
        {
          type: "mc",
          q: "NEGATIVE PREDICTION: 'The restaurant is closed. We ___ eat there tonight.'",
          options: ["aren't going to", "don't", "didn't"],
          a: "aren't going to",
          tip: "Negative prediction: not going to"
        },
        {
          type: "input",
          q: "SUCCESS: 'You studied so hard! You're going to ___ the test!' (pass)",
          a: "pass",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["ace"] },
          tip: "Positive prediction based on effort"
        },
        {
          type: "order",
          q: "Predict the future:",
          words: ["Look", "at", "the", "traffic!", "We're", "going", "to", "be", "late."],
          a: "Look at the traffic! We're going to be late.",
          successtip: "✓ See the problem NOW → predict result with 'going to'!"
        }
      ]
    },

    // ===================================================================
    // SET 3: TIME EXPRESSIONS WITH GOING TO (10 questions) - A2+
    // ===================================================================
    {
      id: "m5-u14-s3",
      title: "Set 3: When Are You Going To...?",
      questions: [
        {
          type: "match",
          q: "Match time expressions:",
          pairs: [
            { left: "Next week/month/year", right: "The following week/month/year" },
            { left: "In two days/weeks/months", right: "Two days/weeks/months from now" },
            { left: "By Friday/next month", right: "Before or on that time" }
          ],
          a: "completed",
          successtip: "✓ Three common patterns for talking about future time!"
        },
        {
          type: "mc",
          q: "NEAR FUTURE: 'I'm going to finish this project ___ week.'",
          options: ["next", "last", "in"],
          a: "next",
          tip: "'Next week' = the week after this one"
        },
        {
          type: "input",
          q: "COUNTING TIME: 'She's going to graduate ___ three months.' (in/after)",
          a: "in",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "'In + number' = that amount of time from now",
          successtip: "✓ 'In three months' = three months from now (counting forward)"
        },
        {
          type: "order",
          q: "Build sentence with time:",
          words: ["We're", "going", "to", "move", "to", "Paris", "next", "year"],
          a: "We're going to move to Paris next year"
        },
        {
          type: "mc",
          q: "DEADLINE: 'I need to finish this ___ Friday.' (before or on Friday)",
          options: ["by", "in", "next"],
          a: "by",
          tip: "'By' = before or on that time (deadline)",
          successtip: "✓ 'By Friday' = anytime before or on Friday (deadline word!)"
        },
        {
          type: "input",
          q: "TOMORROW: 'They're going to arrive ___ tomorrow morning.' (no preposition needed!)",
          a: "tomorrow",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["tomorrow morning", "in the morning"] },
          tip: "Tomorrow, today, tonight = no preposition needed"
        },
        {
          type: "match",
          q: "Match time types:",
          pairs: [
            { left: "Specific future point", right: "next Monday, tomorrow" },
            { left: "Time from now", right: "in two hours, in a week" },
            { left: "Deadline", right: "by 5pm, by next month" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "SOON: 'I'm going to call you ___ a few minutes.'",
          options: ["in", "on", "at"],
          a: "in",
          tip: "'In a few minutes' = a few minutes from now"
        },
        {
          type: "input",
          q: "SEQUENCE: 'First, I'm going to eat. ___ that, I'm going to study.' (after)",
          a: "After",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "'After that' = next in sequence"
        },
        {
          type: "order",
          q: "Complete with deadline:",
          words: ["I'm", "going", "to", "finish", "everything", "by", "next", "Friday"],
          a: "I'm going to finish everything by next Friday",
          successtip: "✓ 'By next Friday' = deadline clear! Everything done before/on that day"
        }
      ]
    },

    // ===================================================================
    // SET 4: OPINIONS ABOUT ENTERTAINMENT (10 questions) - B1
    // ===================================================================
    {
      id: "m5-u14-s4",
      title: "Set 4: What Do You Think?",
      questions: [
        {
          type: "match",
          q: "Expressing opinions about future events:",
          pairs: [
            { left: "I think it's going to be good", right: "Positive prediction" },
            { left: "It looks interesting", right: "Based on appearance" },
            { left: "I don't think I'll like it", right: "Negative opinion" }
          ],
          a: "completed",
          successtip: "✓ Combine opinions + future forms = natural conversation!"
        },
        {
          type: "mc",
          q: "MOVIE OPINION: 'Have you seen the trailer? I think this movie ___ amazing!'",
          options: ["'s going to be", "is", "was"],
          a: "'s going to be",
          tip: "Future prediction about unreleased movie = going to be"
        },
        {
          type: "input",
          q: "NEGATIVE OPINION: 'I don't think I'm going to ___ this show. It looks boring.' (like)",
          a: "like",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["enjoy", "watch"] },
          tip: "'I don't think I'm going to like...' = negative prediction"
        },
        {
          type: "order",
          q: "Build opinion about show:",
          words: ["The", "reviews", "are", "bad.", "It's", "going", "to", "be", "terrible."],
          a: "The reviews are bad. It's going to be terrible.",
          successtip: "✓ Evidence (bad reviews) → prediction (going to be terrible)"
        },
        {
          type: "mc",
          q: "APPEARANCE: 'This restaurant ___ expensive. Look at the menu!'",
          options: ["looks", "is looking", "looked"],
          a: "looks",
          tip: "'Looks' + adjective = appears to be",
          successtip: "✓ 'Looks' = seems/appears. 'It looks good/bad/expensive/interesting'"
        },
        {
          type: "input",
          q: "EXCITEMENT: 'I can't wait! I think the concert is going to be ___!' (amazing)",
          a: "amazing",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["incredible", "fantastic", "awesome", "great"] }
        },
        {
          type: "match",
          q: "Match opinion phrases to uses:",
          pairs: [
            { left: "I think... (going to)", right: "Personal prediction" },
            { left: "It looks... (adjective)", right: "Based on appearance" },
            { left: "I don't think... (going to)", right: "Negative prediction" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "RECOMMENDATION: 'You should see this play. I think you ___ love it!'",
          options: ["'re going to", "will", "can"],
          a: "'re going to",
          tip: "Recommendation with prediction = going to"
        },
        {
          type: "input",
          q: "DOUBT: 'It looks too complicated. I don't think I'm going to ___ it.' (understand)",
          a: "understand",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["get", "like"] }
        },
        {
          type: "order",
          q: "Express opinion naturally:",
          words: ["This", "movie", "looks", "interesting.", "I", "think", "I'm", "going", "to", "watch", "it."],
          a: "This movie looks interesting. I think I'm going to watch it.",
          successtip: "✓ Opinion (looks interesting) → decision (going to watch). Perfect flow!"
        }
      ]
    },

    // ===================================================================
    // SET 5: MAKING PLANS WITH ENTERTAINMENT (10 questions) - B1
    // ===================================================================
    {
      id: "m5-u14-s5",
      title: "Set 5: Let's Make Plans!",
      questions: [
        {
          type: "match",
          q: "Planning entertainment activities:",
          pairs: [
            { left: "What are we going to watch?", right: "Choosing content" },
            { left: "When are we going to go?", right: "Choosing time" },
            { left: "Where are we going to meet?", right: "Choosing place" }
          ],
          a: "completed",
          successtip: "✓ What/When/Where + going to = planning questions!"
        },
        {
          type: "mc",
          q: "MOVIE PLANS: 'Are you going to see the new Marvel movie?' 'Yes! ___ go together?'",
          options: ["Let's", "We", "Are"],
          a: "Let's",
          tip: "'Let's' = suggestion to do something together",
          successtip: "✓ 'Let's go together' = casual, friendly invitation!"
        },
        {
          type: "input",
          q: "INVITATION: 'I'm going to see that new thriller. Do you want to ___?' (come)",
          a: "come",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["join", "come with me", "join me"] }
        },
        {
          type: "order",
          q: "Build entertainment invitation:",
          words: ["I'm", "going", "to", "the", "concert", "Friday.", "Want", "to", "join", "me?"],
          a: "I'm going to the concert Friday. Want to join me?",
          successtip: "✓ State your plan + invite = natural way to ask someone!"
        },
        {
          type: "mc",
          q: "COORDINATING: 'What time ___ meet at the theater?' '7pm works for me.'",
          options: ["should we", "we should", "we"],
          a: "should we",
          tip: "'Should we' = suggesting/coordinating together"
        },
        {
          type: "input",
          q: "GENRE QUESTION: 'What kind of movie are we going to ___?' (watch)",
          a: "watch",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["see"] }
        },
        {
          type: "match",
          q: "Match entertainment vocab to activities:",
          pairs: [
            { left: "Thriller", right: "Exciting, suspenseful movie" },
            { left: "Comedy", right: "Funny movie/show" },
            { left: "Documentary", right: "Real-life/factual film" }
          ],
          a: "completed",
          successtip: "✓ Genre vocabulary from earlier units + going to = planning with detail!"
        },
        {
          type: "mc",
          q: "SUGGESTING GENRE: 'I'm in the mood for something funny. Let's watch a ___.'",
          options: ["comedy", "thriller", "documentary"],
          a: "comedy",
          tip: "Comedy = funny content"
        },
        {
          type: "input",
          q: "AGREEMENT: 'Sounds good! I'm going to ___ the tickets online.' (buy)",
          a: "buy",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["get", "book"] },
          tip: "State what you'll do = taking responsibility"
        },
        {
          type: "order",
          q: "Complete planning conversation:",
          words: ["What", "are", "we", "going", "to", "watch?", "How", "about", "that", "new", "comedy?"],
          a: "What are we going to watch? How about that new comedy?",
          successtip: "✓ Question (going to) + suggestion (how about) = smooth planning!"
        }
      ]
    },

    // ===================================================================
    // SET 6: TRAVEL PLANS (10 questions) - B1
    // ===================================================================
    {
      id: "m5-u14-s6",
      title: "Set 6: Travel Plans & Predictions",
      questions: [
        {
          type: "match",
          q: "Travel planning vocabulary:",
          pairs: [
            { left: "Flight", right: "Airplane trip" },
            { left: "Hotel", right: "Place to stay" },
            { left: "Sightseeing", right: "Visiting tourist attractions" }
          ],
          a: "completed",
          successtip: "✓ Travel words from Unit 29 + going to = vacation planning!"
        },
        {
          type: "mc",
          q: "VACATION PLANS: 'Where are you going to go on vacation?' 'I ___ visit Italy!'",
          options: ["'m going to", "want to", "like to"],
          a: "'m going to",
          tip: "Definite plan = going to",
          successtip: "✓ 'Going to' (not 'want to') shows it's really planned, not just a wish!"
        },
        {
          type: "input",
          q: "BOOKING: 'We're going to ___ our flights tomorrow.' (book/reserve)",
          a: "book",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["reserve", "buy"] }
        },
        {
          type: "order",
          q: "Build travel plan:",
          words: ["I'm", "going", "to", "stay", "in", "Paris", "for", "two", "weeks"],
          a: "I'm going to stay in Paris for two weeks",
          successtip: "✓ 'Going to stay' + duration (for two weeks) = complete plan!"
        },
        {
          type: "mc",
          q: "ACCOMMODATION: 'Where are you going to stay?' 'We're going to stay in a ___.'",
          options: ["hotel", "flight", "sightseeing"],
          a: "hotel",
          tip: "Hotel = place to stay during travel"
        },
        {
          type: "input",
          q: "ACTIVITIES: 'What are you going to do there?' 'We're going to do some ___ and visit museums.' (sightseeing)",
          a: "sightseeing",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["touring", "exploring"] },
          tip: "Sightseeing = visiting tourist attractions"
        },
        {
          type: "match",
          q: "Match travel questions to aspects:",
          pairs: [
            { left: "Where are you going?", right: "Destination" },
            { left: "When are you going?", right: "Time" },
            { left: "How long are you staying?", right: "Duration" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "DURATION: 'How long are you going to stay?' 'We're going to stay ___ three weeks.'",
          options: ["for", "in", "by"],
          a: "for",
          tip: "'For' + duration: for three weeks, for two months",
          successtip: "✓ 'For + time period' = duration. 'In + time period' = when it starts"
        },
        {
          type: "input",
          q: "PREDICTION: 'I think the trip is going to be ___!' (amazing)",
          a: "amazing",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["incredible", "fantastic", "wonderful", "great"] }
        },
        {
          type: "order",
          q: "Complete travel conversation:",
          words: ["I'm", "going", "to", "Japan", "next", "summer.", "I", "can't", "wait!"],
          a: "I'm going to Japan next summer. I can't wait!",
          successtip: "✓ Plan + time + excitement = natural travel conversation! ✈️"
        }
      ]
    },

    // ===================================================================
    // SET 7: COMBINING PLANS & PREDICTIONS (10 questions) - B1+
    // ===================================================================
    {
      id: "m5-u14-s7",
      title: "Set 7: Plans vs Predictions",
      questions: [
        {
          type: "match",
          q: "Identify: Plan or Prediction?",
          pairs: [
            { left: "I'm going to study tonight (Plan)", right: "Decided action" },
            { left: "It's going to rain (Prediction)", right: "Based on clouds" },
            { left: "They're going to win (Prediction)", right: "Based on performance" }
          ],
          a: "completed",
          successtip: "✓ Same structure, different meaning! Context is everything!"
        },
        {
          type: "mc",
          q: "IDENTIFY: 'I'm going to buy a new car next month.' This is a:",
          options: ["Plan (decided action)", "Prediction (based on evidence)", "Both"],
          a: "Plan (decided action)",
          tip: "Next month + personal action = plan"
        },
        {
          type: "input",
          q: "IDENTIFY TYPE: 'Look at the sky! It's going to snow!' This is a ___. (plan/prediction)",
          a: "prediction",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Based on evidence (sky) = prediction"
        },
        {
          type: "order",
          q: "Identify and order the PLAN:",
          words: ["We're", "going", "to", "visit", "our", "grandparents", "this", "weekend"],
          a: "We're going to visit our grandparents this weekend",
          successtip: "✓ Personal decision about future = plan!"
        },
        {
          type: "order",
          q: "Identify and order the PREDICTION:",
          words: ["She", "studied", "so", "hard.", "She's", "going", "to", "pass."],
          a: "She studied so hard. She's going to pass.",
          successtip: "✓ Evidence (studied hard) → prediction (going to pass)!"
        },
        {
          type: "mc",
          q: "BOTH IN ONE: 'I'm going to watch that movie. I think it's going to be great!' Which is which?",
          options: ["First = plan, Second = prediction", "Both plans", "Both predictions"],
          a: "First = plan, Second = prediction",
          tip: "Watch = my plan. Great = my prediction about it",
          successtip: "✓ Often combine: plan (what I'll do) + prediction (what I think will happen)!"
        },
        {
          type: "input",
          q: "CREATE PLAN: 'I'm going to ___ my homework after dinner.' (do)",
          a: "do",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["finish", "complete"] }
        },
        {
          type: "input",
          q: "CREATE PREDICTION: 'The show starts in 5 minutes, and we're stuck in traffic. We're going to be ___!' (late)",
          a: "late",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Current situation → prediction"
        },
        {
          type: "match",
          q: "Match sentences to types:",
          pairs: [
            { left: "I'm going to learn guitar", right: "Plan (personal decision)" },
            { left: "That cake looks delicious!", right: "Opinion (appearance)" },
            { left: "He's going to drop it!", right: "Prediction (evidence)" }
          ],
          a: "completed"
        },
        {
          type: "order",
          q: "Combine plan + prediction naturally:",
          words: ["I'm", "going", "to", "try", "that", "restaurant.", "I", "think", "it's", "going", "to", "be", "good."],
          a: "I'm going to try that restaurant. I think it's going to be good.",
          successtip: "✓ PERFECT! My plan (try) + my prediction (be good). Natural combination!"
        }
      ]
    },

    // ===================================================================
    // SET 8: INVITATIONS WITH GOING TO (10 questions) - B1+
    // ===================================================================
    {
      id: "m5-u14-s8",
      title: "Set 8: Inviting with Future Plans",
      questions: [
        {
          type: "match",
          q: "Invitation patterns with going to:",
          pairs: [
            { left: "I'm going to... Want to join?", right: "State plan + invite" },
            { left: "Are you going to...?", right: "Ask their plans" },
            { left: "We're going to... Come with us!", right: "Group invitation" }
          ],
          a: "completed",
          successtip: "✓ 'Going to' perfect for casual invitations!"
        },
        {
          type: "mc",
          q: "CASUAL INVITE: 'I'm going to the beach tomorrow. ___ come?'",
          options: ["Want to", "Do you", "Will you"],
          a: "Want to",
          tip: "'Want to' after stating your plan = casual invitation",
          successtip: "✓ 'I'm going to [place]. Want to come?' = super natural invitation!"
        },
        {
          type: "input",
          q: "GROUP PLAN: 'We're going to see a movie tonight. Do you want to ___ us?' (join)",
          a: "join",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["come with"] }
        },
        {
          type: "order",
          q: "Build invitation:",
          words: ["I'm", "going", "to", "the", "concert.", "Want", "to", "come", "with", "me?"],
          a: "I'm going to the concert. Want to come with me?",
          successtip: "✓ Plan + invitation = smooth, natural flow!"
        },
        {
          type: "mc",
          q: "CHECKING PLANS: '___ you going to the party on Saturday?' 'Yes, are you?'",
          options: ["Are", "Do", "Will"],
          a: "Are",
          tip: "Question about future plans = Are you going to...?"
        },
        {
          type: "input",
          q: "ACCEPTING: 'I'm going to that new restaurant. Want to join?' 'Yes! What time are we ___?' (going)",
          a: "going",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Accept + ask detail = shows real interest"
        },
        {
          type: "match",
          q: "Match invitations to responses:",
          pairs: [
            { left: "Want to come with us?", right: "Yes, I'd love to!" },
            { left: "Are you going to go?", right: "Yes, I am!" },
            { left: "Let's go together!", right: "Sounds great!" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "DECLINING: 'I'm going to the game. Want to come?' 'Sorry, I can't. I ___ study.'",
          options: ["'m going to", "want to", "have"],
          a: "'m going to",
          tip: "Your plan (excuse) = going to",
          successtip: "✓ 'I'm going to...' = good excuse (shows you have plans)"
        },
        {
          type: "input",
          q: "SUGGESTING TIME: 'I'm going to go shopping tomorrow. When are you ___ to go?' (going)",
          a: "going",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Coordinate timing with 'When are you going to...?'"
        },
        {
          type: "order",
          q: "Complete invitation conversation:",
          words: ["We're", "going", "to", "have", "dinner", "at", "8.", "Can", "you", "make", "it?"],
          a: "We're going to have dinner at 8. Can you make it?",
          successtip: "✓ 'Can you make it?' = Can you come/attend? Perfect invitation ending!"
        }
      ]
    },

    // ===================================================================
    // SET 9: ENTERTAINMENT OPINIONS & PLANS (10 questions) - B1+
    // ===================================================================
    {
      id: "m5-u14-s9",
      title: "Set 9: Discussing Movies & Shows",
      questions: [
        {
          type: "match",
          q: "Combining entertainment vocabulary with going to:",
          pairs: [
            { left: "I'm going to watch a thriller", right: "Plan + genre" },
            { left: "It looks scary", right: "Opinion based on trailer" },
            { left: "I think it's going to be exciting", right: "Prediction about experience" }
          ],
          a: "completed",
          successtip: "✓ Entertainment vocabulary (Units 25, 28) + going to = rich conversations!"
        },
        {
          type: "mc",
          q: "MOVIE CHAT: 'Have you seen the new ___ movie? It's about superheroes.'",
          options: ["action", "documentary", "comedy"],
          a: "action",
          tip: "Superheroes = action movies",
          successtip: "✓ Action = explosions, fights, superheroes. Thriller = suspense, mystery!"
        },
        {
          type: "input",
          q: "RECOMMENDATION: 'You should watch that ___. It's really funny!' (comedy genre)",
          a: "comedy",
          accept: { ignoreCase: true, ignorePunctuation: true }
        },
        {
          type: "order",
          q: "Build opinion + plan:",
          words: ["This", "movie", "looks", "amazing.", "I'm", "going", "to", "see", "it", "tonight."],
          a: "This movie looks amazing. I'm going to see it tonight.",
          successtip: "✓ Opinion (looks amazing) → plan (going to see it). Perfect reasoning!"
        },
        {
          type: "mc",
          q: "NEGATIVE OPINION: 'I don't like ___. They're too sad for me.'",
          options: ["horror movies", "romantic movies", "comedies"],
          a: "romantic movies",
          tip: "Sad = emotional, usually romance or drama",
          successtip: "✓ Express preferences! 'I don't like [genre]' = personal opinion"
        },
        {
          type: "input",
          q: "TRAILER REACTION: 'I just saw the trailer. The special ___ look incredible!' (effects)",
          a: "effects",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Special effects = CGI, explosions, visual magic",
          successtip: "✓ Special effects vocabulary from Unit 25! Plot, villain, hero, effects..."
        },
        {
          type: "match",
          q: "Match genres to characteristics:",
          pairs: [
            { left: "Horror", right: "Scary, frightening" },
            { left: "Comedy", right: "Funny, makes you laugh" },
            { left: "Documentary", right: "Real, educational" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "DISCUSSING: 'What kind of movie are we going to watch?' 'How about a ___? I want to laugh!'",
          options: ["comedy", "thriller", "horror"],
          a: "comedy",
          tip: "Want to laugh = comedy"
        },
        {
          type: "input",
          q: "PLOT OPINION: 'I don't think the ___ is going to be very good. It seems boring.' (story)",
          a: "plot",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["story"] },
          tip: "Plot = story of the movie/show"
        },
        {
          type: "order",
          q: "Complete entertainment discussion:",
          words: ["I", "think", "the", "new", "thriller", "is", "going", "to", "be", "amazing.", "Let's", "go", "see", "it!"],
          a: "I think the new thriller is going to be amazing. Let's go see it!",
          successtip: "✓ Opinion (think going to be) + suggestion (Let's) = persuasive invitation! 🎬"
        }
      ]
    },

    // ===================================================================
    // SET 10: MODULE 5 MASTERY - PULLING IT ALL TOGETHER (10 questions) - B1+
    // ===================================================================
    {
      id: "m5-u14-s10",
      title: "Set 10: Module 5 Grand Finale",
      questions: [
        {
          type: "match",
          q: "Module 5 Journey - What we've learned:",
          pairs: [
            { left: "Units 11-12", right: "Job applications, short questions" },
            { left: "Unit 13", right: "Going out, present continuous future" },
            { left: "Unit 14", right: "Going to, plans, predictions" }
          ],
          a: "completed",
          successtip: "✓ From professional life → social life → future planning. Complete journey!"
        },
        {
          type: "mc",
          q: "COMPREHENSIVE: 'Are you free Friday?' 'Sorry, I can't. I ___ work late, then I ___ meet friends for dinner.'",
          options: ["'m working ... 'm going to", "'m working ... want to", "work ... meet"],
          a: "'m working ... 'm going to",
          tip: "Present continuous (I'm working) + going to (I'm going to meet)",
          successtip: "✓ Combining Unit 13 (present continuous) + Unit 14 (going to)!"
        },
        {
          type: "input",
          q: "FULL CONVERSATION: 'Want to see a movie tomorrow?' 'What are we going to ___?' (watch)",
          a: "watch",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["see"] }
        },
        {
          type: "order",
          q: "Build complete social exchange:",
          words: ["I'm", "going", "to", "that", "new", "restaurant", "tomorrow.", "Want", "to", "join?", "I'd", "love", "to!"],
          a: "I'm going to that new restaurant tomorrow. Want to join? I'd love to!",
          successtip: "✓ Plan + invitation + acceptance = perfect dialogue!"
        },
        {
          type: "mc",
          q: "APPLYING EVERYTHING: Which is most natural?",
          options: ["'I'm going to the concert Friday. Want to come? I think it's going to be great!'", "'I go to concert. Come?'", "'I will go to the concert. Do you want?'"],
          a: "'I'm going to the concert Friday. Want to come? I think it's going to be great!'",
          tip: "Going to + casual invitation + prediction = natural",
          successtip: "✓ Natural English combines: plan (going to), invitation (want to come), prediction (going to be)!"
        },
        {
          type: "input",
          q: "MASTER LEVEL: 'This movie looks boring. I don't think I'm going to ___ it.' (enjoy)",
          a: "enjoy",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["like", "watch"] },
          tip: "Opinion (looks boring) + prediction (not going to enjoy)"
        },
        {
          type: "match",
          q: "Final review - Match to concepts:",
          pairs: [
            { left: "Present continuous for future", right: "I'm meeting friends tomorrow" },
            { left: "Going to for plans", right: "I'm going to study tonight" },
            { left: "Going to for predictions", right: "It's going to rain" }
          ],
          a: "completed",
          successtip: "✓ Three ways to talk about future! Present continuous (arrangements), going to (plans & predictions)"
        },
        {
          type: "mc",
          q: "REAL WORLD: 'I'm going to Paris next summer!' 'Really? What are you going to ___ there?'",
          options: ["do", "doing", "does"],
          a: "do",
          tip: "After 'going to', use base form"
        },
        {
          type: "input",
          q: "PREDICTION + OPINION: 'I think that thriller is going to be ___. The reviews are amazing!' (great/fantastic)",
          a: "amazing",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["great", "fantastic", "incredible", "awesome"] },
          tip: "Evidence (reviews) → prediction"
        },
        {
          type: "order",
          q: "MODULE 5 GRAND FINALE - Build perfect conversation combining everything:",
          words: ["Are", "you", "free", "Saturday?", "I'm", "going", "to", "the", "new", "restaurant.", "I", "think", "it's", "going", "to", "be", "amazing!", "Let's", "go", "together!"],
          a: "Are you free Saturday? I'm going to the new restaurant. I think it's going to be amazing! Let's go together!",
          successtip: "✓ 🎉 MODULE 5 COMPLETE! 🎉 You've mastered: job English, conversation skills, social planning, and future forms! You can now: make plans, give opinions, invite people, predict outcomes, and navigate real social situations in English. From professional interviews to casual Friday night plans—you've got this! Congratulations! 🌟🎬📅🎊"
        }
      ]
    }
  ],

  // ===================================================================
  // READING SET - Weekend Plans Discussion (10 questions)
  // ===================================================================
  // ===================================================================
// READING SET: Planning a Weekend Together
// ===================================================================
readingSet: {
  id: "m5-u14-reading",
  title: "Reading: Planning a Weekend Together",
  category: "reading",

  questions: [
    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 1: Saturday Movie Plans (preguntas 1-3)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Saturday Movie Night**

**Lisa:** Hey everyone! What are you doing this weekend?
**Mark:** I'm not sure yet. I'm thinking about going to the movies.
**Sara:** Oh! I'm going to see that new action movie on Saturday. Want to come with me?
**Mark:** Which one? The superhero movie?
**Sara:** Yes! I think it's going to be amazing. The trailer looks incredible!
**Mark:** I'd love to! What time?
**Sara:** The movie starts at 7pm. Let's meet at the cinema at 6:30.
**Mark:** Perfect! See you then!`
    },
    {
      type: "mc",
      q: "What day is Sara going to see the movie?",
      options: ["Saturday", "Sunday", "Friday"],
      a: "Saturday",
      successtip: "✓ SATURDAY! Perfect day for an action movie. 🍿"
    },
    {
      type: "input",
      q: "What genre is the movie they are going to see?",
      a: "action",
      accept: { ignoreCase: true, ignorePunctuation: true },
      successtip: "✓ ACTION! A superhero movie full of excitement! 💥"
    },
    {
      type: "mc",
      q: "What does Sara think about the movie?",
      options: ["She thinks it's going to be amazing", "She thinks it will be boring", "She hasn't decided"],
      a: "She thinks it's going to be amazing",
      successtip: "✓ 'I think it's going to be amazing' = prediction based on the incredible trailer! 🎯"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 2: Coordinating Schedules (preguntas 4-6)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Sunday Evening Idea**

**Lisa:** That sounds fun! I can't come on Saturday though. I'm visiting my grandmother.
**Sara:** Oh, that's nice! Are you going to stay overnight?
**Lisa:** Yes, I'm going to stay until Sunday afternoon.
**Mark:** What about Sunday evening, Lisa? We could do something then.
**Lisa:** That works! I'm going to be back in town by 5pm.
**Sara:** Great! Let's go to that new restaurant everyone's talking about.
**Lisa:** I've heard about it! I think it's going to be expensive though.
**Mark:** It looks fancy, but the reviews say it's worth it.`
    },
    {
      type: "mc",
      q: "Why is Lisa unable to join them on Saturday?",
      options: ["She's visiting her grandmother", "She's working", "She has another movie plan"],
      a: "She's visiting her grandmother",
      successtip: "✓ FAMILY PLANS!\nShe is out of town until Sunday. 👵"
    },
    {
      type: "input",
      q: "What time will Lisa be back in town on Sunday?",
      a: "5pm",
      accept: { ignoreCase: true, ignorePunctuation: true, alts: ["5", "five", "5 pm"] },
      successtip: "✓ 5:00 PM! Just in time for evening plans. 🕔"
    },
    {
      type: "mc",
      q: "Which grammar form does Lisa use for her fixed plan: 'I'm visiting my grandmother'?",
      options: ["Present continuous for future arrangement", "Going to", "Simple present"],
      a: "Present continuous for future arrangement",
      successtip: "✓ 'I'm visiting' = present continuous! Use it for plans already arranged with other people. 📅"
    },
    {
      type: "passage",
      passage: `**The Reservation**

**Sara:** Sunday evening it is! I'm going to book a table for three people.
**Mark:** Good idea! It's probably going to be busy.
**Lisa:** What time should we meet?
**Sara:** How about 7pm? The restaurant is downtown.
**Mark:** Perfect. I'm really looking forward to it!
**Lisa:** Me too! And after dinner, we could go to that new bar.
**Sara:** Which bar?
**Lisa:** The one near the park. I heard the music is great.
**Mark:** Sounds like a plan! This is going to be a great weekend!`
    },
    {
      type: "mc",
      q: "What specific action is Sara going to take?",
      options: ["Book a table", "Cook dinner", "Buy tickets"],
      a: "Book a table",
      successtip: "✓ BOOK A TABLE!\nEssential for a popular restaurant. ☎️"
    },
    {
      type: "input",
      q: "Why does Mark think they should book? 'Because it's probably going to be ___.'",
      a: "busy",
      accept: { ignoreCase: true, ignorePunctuation: true },
      successtip: "✓ BUSY! Prediction based on the restaurant's popularity. 🍽️👥"
    },
    {
      type: "mc",
      q: "What does Lisa suggest doing after they finish dinner?",
      options: ["Going to a bar", "Going home", "Watching another movie"],
      a: "Going to a bar",
      successtip: "✓ TO A BAR! Specifically the one near the park. 🍸"
    },
    {
      type: "mc",
      q: "COMPREHENSION: What does this conversation primarily demonstrate?",
      options: ["Making plans and predictions using going to and present continuous", "Only past tense stories", "Only present simple facts"],
      a: "Making plans and predictions using going to and present continuous",
      successtip: "✓ PERFECT! You identified: intentions (going to book), arrangements (I'm visiting), and predictions (going to be busy). Native speakers use these daily! 🏆"
    }
  ]
},
  // ===================================================================
  // REVIEW SET (10 questions)
  // ===================================================================
  reviewSet: {
    id: "m5-u14-review",
    title: "Unit 14 Review: Plans, Predictions & Social Life",
    questions: [
      {
        type: "match",
        q: "Review: Going to uses:",
        pairs: [
          { left: "Plans", right: "I'm going to study tonight" },
          { left: "Predictions", right: "It's going to rain" },
          { left: "Same structure!", right: "Context determines meaning" }
        ],
        a: "completed"
      },
      {
        type: "mc",
        q: "PLAN: 'I ___ visit my parents next weekend.'",
        options: ["'m going to", "visit", "visited"],
        a: "'m going to",
        tip: "Future plan = going to"
      },
      {
        type: "input",
        q: "PREDICTION: 'Look at those clouds! It's going to ___!' (rain)",
        a: "rain",
        accept: { ignoreCase: true, ignorePunctuation: true }
      },
      {
        type: "mc",
        q: "TIME: 'I'm going to finish this ___ Friday.' (deadline)",
        options: ["by", "in", "on"],
        a: "by",
        tip: "'By' = before or on that time"
      },
      {
        type: "input",
        q: "OPINION: 'This movie ___ interesting. I want to see it!' (appears)",
        a: "looks",
        accept: { ignoreCase: true, ignorePunctuation: true }
      },
      {
        type: "mc",
        q: "INVITATION: 'I'm going to the concert. ___ come?'",
        options: ["Want to", "Do you", "Are you"],
        a: "Want to",
        tip: "'Want to come?' = casual invitation"
      },
      {
        type: "input",
        q: "NEGATIVE PREDICTION: 'I don't think I'm going to ___ this show.' (like)",
        a: "like",
        accept: { ignoreCase: true, ignorePunctuation: true, alts: ["enjoy"] }
      },
      {
        type: "mc",
        q: "TRAVEL: 'How long are you going to stay?' 'We're going to stay ___ two weeks.'",
        options: ["for", "in", "by"],
        a: "for",
        tip: "'For' + duration"
      },
      {
        type: "order",
        q: "Build complete invitation:",
        words: ["I'm", "going", "to", "that", "new", "restaurant.", "Want", "to", "join", "me?"],
        a: "I'm going to that new restaurant. Want to join me?"
      },
      {
        type: "mc",
        q: "FINAL MASTERY: Best natural conversation?",
        options: ["'I'm going to see that thriller. I think it's going to be scary! Want to come?'", "'I will see movie. Come?'", "'I see thriller tomorrow.'"],
        a: "'I'm going to see that thriller. I think it's going to be scary! Want to come?'",
        successtip: "✓ 🎊 MODULE 5 MASTERY COMPLETE! 🎊 You can now: discuss jobs, make conversation with short questions, plan social activities with present continuous, talk about future with going to, make predictions, express opinions, invite people, and coordinate plans! You've gone from professional contexts to social situations, from past experiences to future plans. You're ready for real-world English in ANY context! Congratulations on completing Module 5! 🌟🎬📅🎉"
      }
    ]
  }
};
